import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Command } from 'cmdk'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import {
  Activity,
  AlertTriangle,
  BadgeCheck,
  CalendarClock,
  Check,
  CheckCircle2,
  Hourglass,
  Moon,
  PanelLeft,
  RefreshCw,
  Rows2,
  Rows3,
  Search,
  Send,
  Sun,
  XCircle,
} from 'lucide-react'
import type { CustomerStatus, LiveState } from '@acs/shared'
import type { CustomerSearchInput } from '@/router'
import { Kbd } from '@/components/ui/kbd'
import { useToast } from '@/components/ui/toast'
import { useUiStore } from '@/stores/ui-store'
import { cn } from '@/lib/cn'

/**
 * Keyboard entry point to the whole console.
 *
 * Everything reachable through the rail is reachable here, plus the three
 * filter views an operator actually asks for by name ("show me what's live",
 * "show me what failed", "show me what went stale"). Those are the questions
 * the legacy panel could only answer by eye.
 *
 * cmdk owns filtering (command-score, so it is fuzzy) and arrow-key movement;
 * this file owns the shortcut, the grouping and the styling.
 */

type IconType = React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>

/* ------------------------------------------------------------------ *
 * Global shortcut
 * ------------------------------------------------------------------ */

/**
 * `Ctrl/Cmd+K` and nothing else.
 *
 * This hook used to claim a bare `/` as well — and so does the customer
 * toolbar, which focuses its search field. Two `window` keydown listeners, one
 * shortcut: whichever mounted last called `preventDefault()` first, so `/`
 * opened the palette on the schedules page and focused the filter box on a
 * customer list, and switching between them changed the answer. A shortcut
 * whose meaning depends on navigation history is worse than no shortcut, so `/`
 * now belongs to the toolbar (where the field it focuses actually is) and the
 * palette keeps the chord its own footer advertises.
 *
 * `Ctrl/Cmd+K` is unambiguous — no text field owns that chord — so it works
 * from anywhere, including from inside an input.
 */
function useCommandShortcut(): void {
  React.useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return
      if (event.altKey) return
      if (!(event.ctrlKey || event.metaKey)) return
      if (event.key !== 'k' && event.key !== 'K') return

      event.preventDefault()
      // Read through getState so the listener never needs re-binding, and so a
      // second Ctrl+K closes the palette instead of re-opening it.
      const store = useUiStore.getState()
      store.setCommandOpen(!store.commandOpen)
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [])
}

/* ------------------------------------------------------------------ *
 * Item
 * ------------------------------------------------------------------ */

// A row is a thumb target on a phone and a dense list entry on a desktop.
const ITEM_CLASS = cn(
  'relative flex h-11 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-[13.5px]',
  'sm:h-9 sm:text-[13px]',
  'text-muted select-none',
  'data-[selected=true]:bg-s3 data-[selected=true]:text-fg',
  'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-40',
  '[&_svg]:size-[15px] [&_svg]:shrink-0'
)

function PaletteItem({
  icon: Icon,
  label,
  hint,
  keywords,
  checked = false,
  value,
  onSelect,
}: {
  icon: IconType
  label: string
  hint?: string
  keywords?: string[]
  checked?: boolean
  value: string
  onSelect: () => void
}) {
  return (
    <Command.Item value={value} keywords={keywords} onSelect={onSelect} className={ITEM_CLASS}>
      <Icon aria-hidden="true" className="text-faint" />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {hint ? <span className="flex-none font-mono text-[11px] text-faint">{hint}</span> : null}
      {checked ? (
        <Check aria-hidden="true" className="flex-none text-accent" />
      ) : null}
    </Command.Item>
  )
}

/* ------------------------------------------------------------------ *
 * Quick filters
 * ------------------------------------------------------------------ */

const QUICK_FILTERS: {
  key: string
  label: string
  icon: IconType
  live: LiveState[]
  keywords: string[]
}[] = [
  {
    key: 'live',
    label: 'Show only live',
    icon: Activity,
    live: ['live'],
    keywords: ['running', 'keep alive', 'active', 'sessions'],
  },
  {
    key: 'failed',
    label: 'Show only failed',
    icon: XCircle,
    live: ['failed'],
    keywords: ['error', 'broken', 'crash'],
  },
  {
    key: 'stale',
    label: 'Show stale sessions',
    icon: AlertTriangle,
    live: ['stale'],
    keywords: ['heartbeat', 'dead worker', 'quiet', 'zombie'],
  },
]

const STATUS_NAV: { status: CustomerStatus; label: string; icon: IconType }[] = [
  { status: 'pending', label: 'Pending customers', icon: Hourglass },
  { status: 'approved', label: 'Approved customers', icon: CheckCircle2 },
  { status: 'confirmed', label: 'Confirmed customers', icon: BadgeCheck },
]

/* ------------------------------------------------------------------ *
 * Palette
 * ------------------------------------------------------------------ */

export function CommandPalette() {
  useCommandShortcut()

  const open = useUiStore((s) => s.commandOpen)
  const setOpen = useUiStore((s) => s.setCommandOpen)
  const theme = useUiStore((s) => s.theme)
  const setTheme = useUiStore((s) => s.setTheme)
  const density = useUiStore((s) => s.density)
  const setDensity = useUiStore((s) => s.setDensity)
  const toggleRail = useUiStore((s) => s.toggleRail)

  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [query, setQuery] = React.useState('')

  // A palette that reopens with the previous query is a palette that shows the
  // previous answer for a frame. Clear on close, not on open.
  React.useEffect(() => {
    if (!open) setQuery('')
  }, [open])

  const run = React.useCallback(
    (action: () => void) => {
      setOpen(false)
      action()
    },
    [setOpen]
  )

  const goCustomers = React.useCallback(
    (status: CustomerStatus, search: CustomerSearchInput) => {
      void navigate({ to: '/customers/$status', params: { status }, search })
    },
    [navigate]
  )

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[80] bg-black/45',
            'transition-opacity duration-100 starting:opacity-0'
          )}
        />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            // Width is a percentage of the *fixed* containing block (the
            // viewport) rather than `100vw`, which counts the scrollbar and can
            // push the page sideways. On a phone that leaves a 12px margin;
            // from `sm` up it settles at the reading width.
            'fixed top-[5dvh] left-1/2 z-[81] -translate-x-1/2 sm:top-[11dvh]',
            'w-[calc(100%-1.5rem)] max-w-[620px]',
            'overflow-hidden rounded-xl border border-line bg-s2 shadow-e3',
            'transition-[opacity,transform] duration-120 ease-out',
            'starting:translate-y-1 starting:opacity-0'
          )}
        >
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>

          <Command
            label="Command palette"
            loop
            className={cn(
              '[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:pb-1',
              '[&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-[10.5px]',
              '[&_[cmdk-group-heading]]:tracking-[0.13em] [&_[cmdk-group-heading]]:text-faint',
              '[&_[cmdk-group-heading]]:uppercase'
            )}
          >
            <div className="flex h-12 items-center gap-2.5 border-b border-line px-3 sm:h-11">
              <Search aria-hidden="true" className="size-[15px] flex-none text-faint" />
              {/* 16px on phones: iOS Safari zooms the page whenever a focused
                  field's text is smaller, and a palette that zooms itself out
                  of frame on tap is not a palette. */}
              <Command.Input
                value={query}
                onValueChange={setQuery}
                autoFocus
                placeholder="Jump to a view, filter or setting…"
                className={cn(
                  'h-full min-w-0 flex-1 bg-transparent text-base text-fg outline-none sm:text-[13.5px]',
                  'placeholder:text-faint'
                )}
              />
              <span className="hidden flex-none sm:inline-flex">
                <Kbd>Esc</Kbd>
              </span>
            </div>

            <Command.List className="max-h-[min(56dvh,420px)] overflow-y-auto overscroll-contain p-1.5">
              <Command.Empty className="px-3 py-8 text-center text-[13px] text-muted">
                Nothing matches “{query}”.
              </Command.Empty>

              <Command.Group heading="Navigation">
                {STATUS_NAV.map((item) => (
                  <PaletteItem
                    key={item.status}
                    value={`nav-${item.status}`}
                    icon={item.icon}
                    label={item.label}
                    keywords={['go to', 'list', 'customers', item.status]}
                    onSelect={() => {
                      run(() => {
                        goCustomers(item.status, {})
                      })
                    }}
                  />
                ))}
                {/*
                  Its own item rather than a `STATUS_NAV` row: that list is
                  keyed by `CustomerStatus` and routed through `goCustomers`,
                  which only builds `/customers/$status`. A destination the rail
                  offers and the palette cannot reach is how the two drift.
                */}
                <PaletteItem
                  value="nav-telegram"
                  icon={Send}
                  label="Telegram customers"
                  keywords={['go to', 'list', 'customers', 'telegram', 'tg', 'bot', 'pending']}
                  onSelect={() => {
                    run(() => {
                      void navigate({ to: '/customers/telegram' })
                    })
                  }}
                />
                <PaletteItem
                  value="nav-schedules"
                  icon={CalendarClock}
                  label="Schedules"
                  keywords={['shift', 'labor demand', 'sites', 'warehouse']}
                  onSelect={() => {
                    run(() => {
                      void navigate({ to: '/schedules' })
                    })
                  }}
                />
              </Command.Group>

              <Command.Group heading="Filters">
                {QUICK_FILTERS.map((filter) => (
                  <PaletteItem
                    key={filter.key}
                    value={`filter-${filter.key}`}
                    icon={filter.icon}
                    label={filter.label}
                    keywords={filter.keywords}
                    onSelect={() => {
                      run(() => {
                        goCustomers('approved', { live: filter.live })
                      })
                    }}
                  />
                ))}
              </Command.Group>

              <Command.Group heading="Theme">
                <PaletteItem
                  value="theme-light"
                  icon={Sun}
                  label="Light theme"
                  checked={theme === 'light'}
                  keywords={['bright', 'day', 'appearance']}
                  onSelect={() => {
                    run(() => {
                      setTheme('light')
                    })
                  }}
                />
                <PaletteItem
                  value="theme-dark"
                  icon={Moon}
                  label="Dark theme"
                  checked={theme === 'dark'}
                  keywords={['night', 'appearance']}
                  onSelect={() => {
                    run(() => {
                      setTheme('dark')
                    })
                  }}
                />
              </Command.Group>

              <Command.Group heading="Actions">
                <PaletteItem
                  value="action-refresh"
                  icon={RefreshCw}
                  label="Refresh all data"
                  hint="invalidates every query"
                  keywords={['reload', 'refetch', 'sync']}
                  onSelect={() => {
                    run(() => {
                      void queryClient.invalidateQueries()
                      toast({ title: 'Refreshing', description: 'Refetching every query.' })
                    })
                  }}
                />
                <PaletteItem
                  value="action-rail"
                  icon={PanelLeft}
                  label="Toggle navigation rail"
                  keywords={['sidebar', 'collapse', 'expand']}
                  onSelect={() => {
                    run(toggleRail)
                  }}
                />
                <PaletteItem
                  value="action-density"
                  icon={density === 'compact' ? Rows2 : Rows3}
                  label={density === 'compact' ? 'Comfortable rows' : 'Compact rows'}
                  keywords={['density', 'spacing', 'row height']}
                  onSelect={() => {
                    run(() => {
                      setDensity(density === 'compact' ? 'comfortable' : 'compact')
                    })
                  }}
                />
              </Command.Group>
            </Command.List>

            {/* Keycaps for a keyboard the phone does not have. The whole strip
                is desktop-only. */}
            <div className="hidden items-center gap-3 border-t border-line bg-s1 px-3 py-2 text-[11.5px] text-faint sm:flex">
              <span className="flex items-center gap-1.5">
                <Kbd>↑</Kbd>
                <Kbd>↓</Kbd>
                navigate
              </span>
              <span className="flex items-center gap-1.5">
                <Kbd>↵</Kbd>
                select
              </span>
              <span className="ml-auto flex items-center gap-1.5">
                <Kbd>Ctrl K</Kbd>
                toggle
              </span>
            </div>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
